import React, { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { FormValues } from '../../UpsertGrantModal.meta';
import { useStockOptionsContext } from '../../../../context/useStockOptionsContext';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { GranTypeDTO } from '../../../../../../Profile/Accounts/api/useAccountsQuery';
import { Input } from '../../../../../../CommonComponents/OPL/Input/Input';
import { EXCLUDE_OPTION_VALUE, excludeOption } from './Summary.meta';

interface Props {
  isAddingNewGrant?: boolean;
}

export const Summary = ({ isAddingNewGrant }: Props) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    control,
    watch,
    register,
    formState: { errors },
    setValue,
  } = useFormContext<FormValues>();
  const { options } = useStockOptionsContext();

  const [grantType, managementStyleId] = watch(['grantType', 'orionManagementStyleId']);

  const watchedOwnerModel = useWatch({
    name: 'ownerModel',
    control: control,
  });

  const ownerOptions = [
    {
      label: 'People',
      options: options.OwnerOptions.People.map(owner => ({
        value: owner.Id,
        label: owner.Name,
      })),
    },
    {
      label: 'Trusts',
      options: options.OwnerOptions.Trusts.map(owner => ({
        value: owner.Id,
        label: owner.Name,
      })),
    },
  ];

  const managementStyleOptions = options.ManagementStyles.map(option => ({
    value: option.id,
    label: option.name,
    defaultBucket: option.defaultBucket,
  }));

  const managementStyleSelectedOption = managementStyleOptions.find(
    option => option.value === managementStyleId,
  );

  const availableAssetBucketOptions = options.AvailableAssetBuckets.map(({ Title }, index) => ({
    label: Title,
    value: index,
  }));

  const assetBucketOptions = [...availableAssetBucketOptions, excludeOption].map(option => {
    if (options.AssetBucketingByManagementStyle) {
      const isDefault = !managementStyleSelectedOption
        ? option.value === EXCLUDE_OPTION_VALUE
        : managementStyleSelectedOption.defaultBucket === null
        ? option.value === EXCLUDE_OPTION_VALUE
        : option.value === managementStyleSelectedOption.defaultBucket;

      return {
        ...option,
        rightLabel: isDefault ? 'Default' : undefined,
      };
    }

    return option;
  });

  return (
    <Box display="flex" flexDirection="column" pt="12" css="gap: 16px">
      <Input
        {...register('name')}
        label="Company Name"
        maxLength={144}
        disabled={isAddingNewGrant}
        formVariant={errors.name ? 'error' : undefined}
        helperText={errors.name?.message}
      />

      <Controller
        name="owner"
        control={control}
        render={({ field: { value, onChange, ref } }) => (
          <Select
            innerRef={ref}
            label="Owner"
            value={ownerOptions
              .map(({ options }) => options)
              .flat()
              .find(option => option.label === value)}
            options={ownerOptions}
            onChange={opt => {
              onChange(opt?.label);
              if (opt) {
                const selectedOwnerOption = Object.values(options.OwnerOptions)
                  .map(items => items)
                  .flat()
                  .find(option => option.Name === opt.label);
                if (selectedOwnerOption) {
                  setValue('ownerModel', [
                    {
                      ...watchedOwnerModel[0],
                      Id: opt.value,
                      Name: opt.label,
                      OwnerType: selectedOwnerOption.OwnerType,
                      Percentage: 1,
                    },
                  ]);
                }
              }
            }}
            disabled={isAddingNewGrant}
            formVariant={errors.owner ? 'error' : undefined}
            helperText={errors.owner?.message}
            isPortal
          />
        )}
      />

      <Controller
        name="grantType"
        control={control}
        render={({ field: { value, onChange, ref } }) => (
          <Select
            innerRef={ref}
            label="Asset Type"
            value={options.GrantTypeOptions.find(option => option.value === value) || null}
            options={options.GrantTypeOptions}
            onChange={option => onChange(option?.value)}
            disabled={isAddingNewGrant}
            isPortal
          />
        )}
      />

      <Controller
        control={control}
        name="marketValue"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            label="Market Value"
            leftIcon="far fa-dollar-sign"
            decimalScale={2}
            allowNegative={false}
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            {...field}
          />
        )}
      />

      <Controller
        control={control}
        name="totalShares"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            label="Total Shares"
            decimalScale={0}
            allowNegative={false}
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            formVariant={errors.totalShares ? 'error' : undefined}
            helperText={errors.totalShares?.message}
            {...field}
          />
        )}
      />

      {options.AssetBucketingByManagementStyle && (
        <Controller
          name="orionManagementStyleId"
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              innerRef={ref}
              label="Management Style"
              value={managementStyleOptions.find(option => option.value === value) ?? null}
              options={managementStyleOptions}
              onChange={option => {
                const assetBucketIndex = managementStyleOptions.find(
                  managementStyleOption => managementStyleOption.value === option?.value,
                );

                if (assetBucketIndex) {
                  setValue(
                    'customAssetBucketIndex',
                    assetBucketIndex.defaultBucket !== null
                      ? assetBucketIndex.defaultBucket
                      : EXCLUDE_OPTION_VALUE,
                  );
                }

                onChange(option?.value);
              }}
              isPortal
            />
          )}
        />
      )}

      {options.DisplayAssetBucketing && (
        <Controller
          name="customAssetBucketIndex"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <Select
                label="Asset Bucketing"
                value={assetBucketOptions.find(option => option.value === value) || null}
                options={assetBucketOptions}
                onChange={option => onChange(option?.value)}
                disabled={
                  !options.AllowAssetBucketingCustomization ||
                  (options.AssetBucketingByManagementStyle && managementStyleId === null)
                }
                isPortal
              />
            );
          }}
        />
      )}

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Controller
            name="grantYear"
            control={control}
            render={({ field: { value, onChange, ref } }) => (
              <Select
                isClearable
                innerRef={ref}
                label="Grant Year"
                value={value ? { value, label: value.toString() } : null}
                options={options.GrantYearOptions.map(year => ({
                  value: year,
                  label: year.toString(),
                }))}
                onChange={option => onChange(option?.value)}
                isPortal
              />
            )}
          />

          {(grantType === GranTypeDTO.Iso || grantType === GranTypeDTO.Nso) && (
            <Controller
              control={control}
              name="pricePerShare"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Strike Price"
                  leftIcon="far fa-dollar-sign"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  {...field}
                />
              )}
            />
          )}

          <Controller
            control={control}
            name="expectedReturn"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Projected Return"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip="Enter the average annual rate of return expected on this asset. If left blank, we will use the return for the selected asset class below."
                {...field}
              />
            )}
          />

          <Controller
            name="assetClass"
            control={control}
            render={({ field: { value, onChange, ref } }) => (
              <Select
                isClearable
                innerRef={ref}
                label="Asset Class"
                value={options.AssetClassOptions.find(option => option.value === value) || null}
                options={options.AssetClassOptions}
                onChange={option => onChange(option?.value)}
                disabled={isAddingNewGrant}
                isPortal
              />
            )}
          />

          <Controller
            control={control}
            name="earmarks"
            render={({ field: { value, onChange } }) => {
              return (
                <Select
                  isMulti
                  label="Account Earmark"
                  value={value}
                  options={options.EarmarkOptions}
                  onChange={earmarks => onChange(earmarks)}
                  isPortal
                />
              );
            }}
          />
        </>
      )}
    </Box>
  );
};
